import { z } from 'zod';

import { isValidHandle, normalizeHandle } from '../../shared/auth/student-login';
import { E164_RE } from '../../shared/lib/phone';

// Form-friendly schemas: every field is a string at the input layer; numeric
// + null coercion happens in onSubmit so react-hook-form generics stay one-arg.

export const EXAM_TYPES = ['academic', 'general'] as const;
export type ExamTypeValue = (typeof EXAM_TYPES)[number];

const E164 = E164_RE;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const targetBandField = z
  .string()
  .trim()
  .refine(
    (v) => v === '' || (!Number.isNaN(parseFloat(v)) && parseFloat(v) >= 0 && parseFloat(v) <= 9),
    'Target band must be between 0 and 9',
  );

export const inviteStudentSchema = z.object({
  email: z.string().trim().toLowerCase().email('Enter a valid email'),
  name: z.string().trim().min(2, 'Name must be at least 2 characters').max(80),
  phone: z.string().trim().regex(E164, 'Phone must be E.164, e.g. +9779812345678'),
  roll_no: z.string().trim().max(40),
  exam_type: z.enum(EXAM_TYPES),
  target_band: targetBandField,
});
export type InviteStudentFormInput = z.infer<typeof inviteStudentSchema>;

// Phase 90 — Add-student dialog with two modes in one form:
//   'credential' — username + password login, no email (the eased-onboarding path)
//   'email'      — the classic magic-link invite
// Fields are shared; mode-specific requirements are enforced in superRefine so
// the resolver can stay a single instance regardless of the selected mode.
export const ADD_STUDENT_MODES = ['credential', 'email'] as const;
export type AddStudentMode = (typeof ADD_STUDENT_MODES)[number];

export const addStudentSchema = z
  .object({
    mode: z.enum(ADD_STUDENT_MODES),
    name: z.string().trim().min(2, 'Name must be at least 2 characters').max(80),
    email: z.string().trim().toLowerCase(),
    username: z.string().trim().max(40),
    phone: z.string().trim(),
    roll_no: z.string().trim().max(40),
    exam_type: z.enum(EXAM_TYPES),
    target_band: targetBandField,
  })
  .superRefine((v, ctx) => {
    if (v.mode === 'email') {
      if (!EMAIL_RE.test(v.email)) {
        ctx.addIssue({ code: 'custom', path: ['email'], message: 'Enter a valid email' });
      }
      if (!E164.test(v.phone)) {
        ctx.addIssue({
          code: 'custom',
          path: ['phone'],
          message: 'Phone must be E.164, e.g. +9779812345678',
        });
      }
    } else {
      if (!isValidHandle(normalizeHandle(v.username))) {
        ctx.addIssue({
          code: 'custom',
          path: ['username'],
          message: 'Username must be 3-40 chars (letters, numbers, . _ -)',
        });
      }
      if (v.phone !== '' && !E164.test(v.phone)) {
        ctx.addIssue({
          code: 'custom',
          path: ['phone'],
          message: 'Phone must be E.164, e.g. +9779812345678',
        });
      }
    }
  });
export type AddStudentFormInput = z.infer<typeof addStudentSchema>;

// Phase 133 — edit the counsellor-owned fields from the student profile.
// Deliberately NOT the name/email/username: those are identity and are changed
// through the credential-reset path, not a details form. Every field may be
// cleared, so blank is valid throughout and means "unset", not "unchanged".
export const editStudentProfileSchema = z.object({
  roll_no: z.string().trim().max(40, 'Roll number must be 40 characters or fewer'),
  target_band: targetBandField,
  exam_type: z.enum(EXAM_TYPES),
  exam_date: z
    .string()
    .trim()
    .refine((v) => v === '' || !Number.isNaN(Date.parse(v)), 'Enter a valid date'),
  phone: z
    .string()
    .trim()
    .refine((v) => v === '' || E164.test(v), 'Phone must be E.164, e.g. +9779812345678'),
});
export type EditStudentProfileInput = z.infer<typeof editStudentProfileSchema>;

// Phase 77 — invite a staff member (team management). Roles an institute may
// grant via self-service; `owner` is set at institute creation, not here.
// Phone is optional for staff (unlike students) — it only seeds phone_verified.
export const STAFF_ROLES = ['teacher', 'admin', 'examiner'] as const;
export type StaffRoleValue = (typeof STAFF_ROLES)[number];

export const inviteStaffSchema = z.object({
  email: z.string().trim().toLowerCase().email('Enter a valid email'),
  name: z.string().trim().min(2, 'Name must be at least 2 characters').max(80),
  phone: z
    .string()
    .trim()
    .refine((v) => v === '' || E164.test(v), 'Phone must be E.164, e.g. +9779812345678'),
  role: z.enum(STAFF_ROLES),
});
export type InviteStaffFormInput = z.infer<typeof inviteStaffSchema>;

// Phase 12 — new-session form. Datetime-local inputs return naive strings
// ("YYYY-MM-DDTHH:mm") that we widen to ISO at submit. Duration is computed
// from a numeric minutes input; the server validates end > start.
export const SESSION_AUDIENCE = ['batch', 'students'] as const;
export type SessionAudience = (typeof SESSION_AUDIENCE)[number];

// Phase 127 — AI Writing/Speaking review for this sitting. 'inherit' maps to a
// NULL override so the institute default applies; the form keeps it a string
// like every other field and coerces at submit.
export const SESSION_AI_REVIEW = ['inherit', 'on', 'off'] as const;
export type SessionAiReviewChoice = (typeof SESSION_AI_REVIEW)[number];

/** Form choice → the nullable override the RPC takes. */
export function aiReviewChoiceToValue(choice: SessionAiReviewChoice): boolean | null {
  if (choice === 'on') return true;
  if (choice === 'off') return false;
  return null;
}

export const newSessionSchema = z
  .object({
    test_id: z.string().uuid({ message: 'Pick a test' }),
    scheduled_start: z.string().min(1, 'Pick a start date and time'),
    duration_minutes: z
      .string()
      .trim()
      .refine((v) => {
        const n = parseInt(v, 10);
        return Number.isInteger(n) && n >= 30 && n <= 600;
      }, 'Duration must be 30–600 minutes'),
    location: z.string().trim().max(120, 'Location must be 120 characters or fewer'),
    audience: z.enum(SESSION_AUDIENCE),
    batch_id: z.string().trim(),
    student_ids: z.array(z.string().uuid()).default([]),
    ai_review: z.enum(SESSION_AI_REVIEW).default('inherit'),
  })
  .superRefine((val, ctx) => {
    if (val.audience === 'batch' && val.batch_id === '') {
      ctx.addIssue({
        code: 'custom',
        path: ['batch_id'],
        message: 'Pick a batch',
      });
    }
    if (val.audience === 'students' && val.student_ids.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['student_ids'],
        message: 'Pick at least one student',
      });
    }
  });
export type NewSessionFormInput = z.input<typeof newSessionSchema>;
export type NewSessionFormOutput = z.output<typeof newSessionSchema>;

// Phase 11 — batch editor form. Schedule label is optional; name is required
// and must be unique within the institute (enforced by the DB unique index).
export const batchEditorSchema = z.object({
  name: z.string().trim().min(2, 'Name must be at least 2 characters').max(80),
  schedule_label: z.string().trim().max(80, 'Keep schedule under 80 characters'),
});
export type BatchEditorInput = z.infer<typeof batchEditorSchema>;

// CSV row schema — same shape as the form, but exam_type defaults to
// 'academic' when blank, and target_band/roll_no are optional.
export const csvRowSchema = z.object({
  email: z.string().trim().toLowerCase().email('Invalid email'),
  name: z.string().trim().min(2, 'Name 2-80 chars').max(80),
  phone: z.string().trim().regex(E164, 'Phone must be E.164'),
  roll_no: z.string().trim().max(40).optional().default(''),
  exam_type: z
    .string()
    .trim()
    .toLowerCase()
    .optional()
    .default('academic')
    .refine((v) => v === 'academic' || v === 'general', 'exam_type must be academic or general'),
  target_band: targetBandField.optional().default(''),
});
export type CsvRowInput = z.infer<typeof csvRowSchema>;

// Phase 90 — CSV row for credential provisioning (no email; username instead).
export const credentialCsvRowSchema = z.object({
  username: z
    .string()
    .trim()
    .max(40)
    .refine(
      (v) => isValidHandle(normalizeHandle(v)),
      'username must be 3-40 chars (letters, numbers, . _ -)',
    ),
  name: z.string().trim().min(2, 'Name 2-80 chars').max(80),
  phone: z
    .string()
    .trim()
    .optional()
    .default('')
    .refine((v) => v === '' || E164.test(v), 'Phone must be E.164'),
  roll_no: z.string().trim().max(40).optional().default(''),
  exam_type: z
    .string()
    .trim()
    .toLowerCase()
    .optional()
    .default('academic')
    .refine((v) => v === 'academic' || v === 'general', 'exam_type must be academic or general'),
  target_band: targetBandField.optional().default(''),
});
export type CredentialCsvRowInput = z.infer<typeof credentialCsvRowSchema>;

// Phase 157 — create an assignment (Phase 142's "assign homework", renamed).
//
// SESSION_AUDIENCE is deliberately NOT reused any more. A session is genuinely
// one-or-the-other; an assignment targets any number of batches UNIONED with
// any number of hand-picked students, so there is no audience to discriminate
// on — only "did you pick anybody at all".
export const createAssignmentSchema = z
  .object({
    test_id: z.string().uuid({ message: 'Pick a test' }),
    due_date: z.string().min(1, 'Pick a due date'),
    instructions_md: z.string().trim().max(4000, 'Keep instructions under 4000 characters'),
    batch_ids: z.array(z.string().uuid()).default([]),
    student_ids: z.array(z.string().uuid()).default([]),
    sync_batches: z.boolean().default(true),
  })
  .superRefine((val, ctx) => {
    if (val.batch_ids.length === 0 && val.student_ids.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['batch_ids'],
        message: 'Pick at least one batch or student',
      });
    }
  });
export type CreateAssignmentFormInput = z.input<typeof createAssignmentSchema>;
export type CreateAssignmentFormOutput = z.output<typeof createAssignmentSchema>;
